import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { getHabitPresets } from '@/lib/firebase/queries/habitPresets';
import type { HabitPreset, HabitCategory } from '@/types/habits';
import { useLanguage } from '@/contexts/LanguageContext';
import { habitBadges, HABIT_CATEGORY_ORDER } from '@/lib/constants/habitBadges';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface HabitPresetSelectorProps {
  onSelectPreset: (preset: HabitPreset) => void;
}

export function HabitPresetSelector({ onSelectPreset }: HabitPresetSelectorProps) {
  const [presets, setPresets] = useState<HabitPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<HabitCategory>>(
    new Set() // Start with all categories collapsed
  );
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      setLoading(true);
      const fetchedPresets = await getHabitPresets();
      setPresets(fetchedPresets);
    } catch (error) {
      console.error('Error fetching habit presets:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category: HabitCategory) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Group presets by category
  const presetsByCategory = HABIT_CATEGORY_ORDER.reduce((acc, category) => {
    acc[category] = presets.filter(p => p.category === category);
    return acc;
  }, {} as Record<HabitCategory, HabitPreset[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (presets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-muted-foreground">
        <p className={`text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
          {isRTL ? 'لا توجد عادات محددة مسبقاً' : 'No preset habits available'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ScrollArea className="flex-1">
        <div className="space-y-2 pt-2">
          {HABIT_CATEGORY_ORDER.map(category => {
            const categoryPresets = presetsByCategory[category];
            if (categoryPresets.length === 0) return null;

            const isExpanded = expandedCategories.has(category);

            return (
              <Collapsible
                key={category}
                open={isExpanded}
                onOpenChange={() => toggleCategory(category)}
              >
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    "w-full justify-between hover:bg-background/80 h-auto py-2.5 px-4 text-sm bg-background shadow-sm",
                    isRTL && "flex-row-reverse"
                  )}
                >
                  <span className={cn("flex items-center gap-2", isRTL && "flex-row-reverse")}>
                    <span className="text-base">{habitBadges[category].icon}</span>
                    <span className="font-medium">{t(`habits.categories.${category}`)}</span>
                    <span className="text-xs text-muted-foreground">
                      ({categoryPresets.length})
                    </span>
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 pt-1">
                {categoryPresets.map(preset => (
                  <Button
                    type="button"
                    key={preset.id}
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-left hover:bg-background/50 h-auto py-3 px-4 bg-transparent border border-border",
                      isRTL && "text-right justify-end"
                    )}
                    onClick={() => onSelectPreset(preset)}
                  >
                    <div className={cn("flex flex-col gap-1 w-full", isRTL ? "items-end text-right" : "items-start text-left")}>
                      <span className="font-medium text-sm">
                        {isRTL && preset.nameAr ? preset.nameAr : preset.name}
                      </span>
                      {((isRTL && preset.descriptionAr) || preset.description) && (
                        <span className="text-xs text-muted-foreground whitespace-normal break-words">
                          {isRTL && preset.descriptionAr ? preset.descriptionAr : preset.description}
                        </span>
                      )}
                    </div>
                  </Button>
                ))}
              </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

